import React from 'react';
import { startCase } from 'lodash';
import {
  Stack,
  Button,
  Icon,
  Dot,
  type DotProps,
  Popover,
  Card,
  Clickable,
  P,
  ForEach,
} from '@fashionnovateam/nova-ui-toolkit';
import getPoStatus from '../../Lib/Utils/Helpers/make-button-text';
import PoStatus from '../../Lib/Constants/purchase-order-status';
import {
  PurchaseOrder,
  BulkProdSubStage,
} from '../../Lib/Types/purchase-order';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';

export type PoStatusProps = {
  purchaseOrder: PurchaseOrder;
  handleAcceptModal?: () => void;
  handleRejectModal?: () => void;
  onPoSubStageUpdate?: (sub_stage: BulkProdSubStage) => void;
  details?: boolean;
  perms?: {
    canAccept: boolean;
    canReject: boolean;
    canUpdateBulkStatus: boolean;
  };
};

function StatusLabelData({ purchaseOrder }: { purchaseOrder: PurchaseOrder }) {
  const { flags } = React.useContext(FeatureFlagsContext);
  const statusColorMap: { [key: string]: DotProps['color'] } = {
    'Pending Supervisor Approval': 'yellow',
    'Rejected by Supervisor': 'red',
    Closed: 'neutral',
    'Pending Receipt': 'primary',
    'Fully Billed': 'green',
  };

  const subStageColorMap: { [key: string]: DotProps['color'] } = {
    'Pending Accepentance': 'yellow',
    'Pre-Prod: Rejected': 'red',
    'TOP: Approved': 'green',
    'TOP: Rejected': 'red',
    'TOP: On Hold': 'yellow',
    'Delivery Date: Pending': 'yellow',
    'QC Failed: Returned': 'red',
    'QC: Partially Received': 'yellow',
    'Payment Pending': 'yellow',
    'Fully Billed': 'green',
  };

  let labelToDisplay = '';
  let colorToDisplay: DotProps['color'] = 'primary';

  if (
    flags.cancelled_po &&
    flags.cancelled_po.enabled &&
    purchaseOrder.is_cancelled
  ) {
    labelToDisplay = 'Cancelled';
    colorToDisplay = 'red';
  } else if (purchaseOrder.status !== 'Closed') {
    labelToDisplay = purchaseOrder.sub_stage || '';
    colorToDisplay = subStageColorMap[purchaseOrder.sub_stage || ''];
  } else {
    labelToDisplay = purchaseOrder.status;
    colorToDisplay = statusColorMap[purchaseOrder.status];
  }

  return (
    <>
      <Dot color={colorToDisplay} />
      {labelToDisplay}
    </>
  );
}

function StatusLabel({
  onPoSubStageUpdate,
  purchaseOrder,
  details,
  perms,
}: PoStatusProps) {
  const showBulkProductionButton =
    details &&
    perms?.canUpdateBulkStatus &&
    purchaseOrder.stage === 'Bulk Production' &&
    !purchaseOrder.is_cancelled &&
    onPoSubStageUpdate;

  const bulkProdList: BulkProdSubStage[] = [
    'Ready for Production',
    'Buy Materials',
    'Cutting/Sewing',
    'Washing',
    'Packaging',
  ];

  return showBulkProductionButton ? (
    <Popover position="bottom-right" offset={8}>
      <Popover.Clickable>
        <Stack
          direction="row"
          gap="xs"
          align="center"
          className="bg-neutral py-2 px-3 rounded text-xs border border-neutral-200 shadow-[0_2px_3px_0_rgba(0,0,0,0.04),0_1px_1px_0_rgba(0,0,0,0.06)]"
        >
          <StatusLabelData purchaseOrder={purchaseOrder} />
        </Stack>
      </Popover.Clickable>
      <Popover.Content>
        {({ close }) => (
          <Card className="w-[303px] text-[#1D2433] text-xsm absolute">
            <Card.Body>
              <ForEach items={bulkProdList}>
                {(bulkSubStage) => (
                  <Clickable
                    key={bulkSubStage}
                    onClick={() => {
                      onPoSubStageUpdate(bulkSubStage);
                      close();
                    }}
                    className="h-9 self-stretch hover:bg-neutral-100 px-3 py-2"
                  >
                    <Stack
                      direction="row"
                      align="center"
                      className="gap-[10px]"
                    >
                      <P>{bulkSubStage}</P>
                      {purchaseOrder.sub_stage === bulkSubStage && (
                        <Icon.CheckTickCircle
                          className="ml-auto mr-0 size-5"
                          variant="solid"
                          color="#5247DF"
                        />
                      )}
                    </Stack>
                  </Clickable>
                )}
              </ForEach>
            </Card.Body>
            <Card.Footer className="py-2">
              <Clickable
                onClick={() => {
                  onPoSubStageUpdate('Production Completed');
                  close();
                }}
                className="self-stretch hover:bg-neutral-100 px-3 py-2"
              >
                <Stack direction="row" align="center" className="gap-[10px]">
                  <P>Production Completed</P>
                  {purchaseOrder.sub_stage === 'Production Completed' && (
                    <Icon.LoadingCircle
                      className="ml-auto mr-0 size-5"
                      color="#5247DF"
                    />
                  )}
                </Stack>
              </Clickable>
            </Card.Footer>
          </Card>
        )}
      </Popover.Content>
    </Popover>
  ) : (
    <Stack
      direction="row"
      gap="xs"
      width="fit"
      align="center"
      className="bg-neutral-100 p-2 rounded text-xs"
    >
      <StatusLabelData purchaseOrder={purchaseOrder} />
    </Stack>
  );
}

export function PoStatusComponent({
  purchaseOrder,
  handleAcceptModal,
  handleRejectModal,
  onPoSubStageUpdate,
  details,
  perms,
}: PoStatusProps) {
  const { flags } = React.useContext(FeatureFlagsContext);
  const acceptButtonText = getPoStatus({
    revised_at: purchaseOrder.revised_at,
    vendor_rejection_reason: purchaseOrder.vendor_rejection_reason,
    revision_accepted_at: purchaseOrder.revision_accepted_at,
  });

  const acceptDisabled =
    acceptButtonText === PoStatus.ACCEPTED ||
    acceptButtonText === PoStatus.PENDING;

  return (
    <Stack direction="row" gap="sm" width="fit" align="center">
      <StatusLabel
        details={details}
        perms={perms}
        purchaseOrder={purchaseOrder}
        onPoSubStageUpdate={onPoSubStageUpdate}
      />
      {!purchaseOrder.is_accepted &&
        (flags.cancelled_po?.enabled === false ||
          purchaseOrder.is_cancelled === false) && (
          <>
            {handleAcceptModal &&
              perms?.canAccept &&
              acceptButtonText !== PoStatus.ACCEPTED && (
                <Button
                  size="sm"
                  onClick={handleAcceptModal}
                  disabled={acceptDisabled}
                >
                  <Icon
                    name="check/check-tick-circle"
                    className="size-4 z-50"
                    variant="solid"
                  />
                  {startCase(acceptButtonText)}
                </Button>
              )}
            {handleRejectModal && perms?.canReject && (
              <Button size="sm" color="secondary" onClick={handleRejectModal}>
                <Icon
                  name="multiple-cross/multiple-cross-cancel-square"
                  variant="solid"
                />
                Reject PO
              </Button>
            )}
          </>
        )}
      {/* Button will be enabled later */}
    </Stack>
  );
}
